import type { PetEntity } from 'src/game/logic/EntityManager';
import type { AccessorySlot } from 'src/replicant/data/AccessoryItems.schema';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';

/** Asset directory containing the Dream Drift hero costume pieces. */
const dreamDriftCostumesRoot = 'assets/ui/dreamDrift/costumes';

/** Accessory slots supported by the pet rendering system. */
const petAccessorySlots: readonly AccessorySlot[] = [1, 2, 3, 4, 5];

/** Shared display color associated with each Dream Drift hero. */
const dreamDriftHeroColors: Record<DreamDriftHero, string> = {
  rumbleHero: '#FFE611',
  whirlwindDancer: '#B2E1FF',
  skySummoner: '#E4C7FF',
  prismPainter: '#9FFF8F',
};

/** Costume pieces with required cape backwear as the universal fallback. */
type DreamDriftCostume = Partial<Record<AccessorySlot, string>> &
  Record<5, string>;

/** Costume artwork mapped to the hero that wears it. */
const dreamDriftCostumes: Record<DreamDriftHero, DreamDriftCostume> = {
  rumbleHero: {
    1: `${dreamDriftCostumesRoot}/01_rumblehero/01_headwear.png`,
    2: `${dreamDriftCostumesRoot}/01_rumblehero/02_eyewear.png`,
    3: undefined,
    4: `${dreamDriftCostumesRoot}/01_rumblehero/04_neckwear.png`,
    5: `${dreamDriftCostumesRoot}/01_rumblehero/05_backwear.png`,
  },
  whirlwindDancer: {
    1: `${dreamDriftCostumesRoot}/02_whirlwinddancer/01_headwear.png`,
    2: `${dreamDriftCostumesRoot}/02_whirlwinddancer/02_eyewear.png`,
    3: undefined,
    4: `${dreamDriftCostumesRoot}/02_whirlwinddancer/04_neckwear.png`,
    5: `${dreamDriftCostumesRoot}/02_whirlwinddancer/05_backwear.png`,
  },
  skySummoner: {
    1: `${dreamDriftCostumesRoot}/03_skysummoner/01_headwear.png`,
    2: `${dreamDriftCostumesRoot}/03_skysummoner/02_eyewear.png`,
    3: undefined,
    4: `${dreamDriftCostumesRoot}/03_skysummoner/04_neckwear.png`,
    5: `${dreamDriftCostumesRoot}/03_skysummoner/05_backwear.png`,
  },
  prismPainter: {
    1: `${dreamDriftCostumesRoot}/04_prismpainter/01_headwear.png`,
    2: `${dreamDriftCostumesRoot}/04_prismpainter/02_eyewear.png`,
    3: undefined,
    4: `${dreamDriftCostumesRoot}/04_prismpainter/04_neckwear.png`,
    5: `${dreamDriftCostumesRoot}/04_prismpainter/05_backwear.png`,
  },
};

/** All costume images that must be loaded before displaying dressed pets. */
export const dreamDriftCostumeAssets = Object.values(
  dreamDriftCostumes,
).flatMap((costume) =>
  Object.values(costume).filter(
    (asset): asset is string => asset !== undefined,
  ),
);

/**
 * Get the shared display color for one Dream Drift hero.
 *
 * @param hero Dream Drift hero whose color should be returned.
 * @returns Hexadecimal display color associated with the hero.
 */
export function dreamDriftGetHeroColor(hero: DreamDriftHero): string {
  // Use the icon palette as the shared color source for every hero surface.
  return dreamDriftHeroColors[hero];
}

/**
 * Dress a pet with one Dream Drift hero costume.
 *
 * @param petEntity Pet entity whose accessory substitutions should be replaced.
 * @param hero Dream Drift hero identifying the costume to apply.
 * @returns Nothing.
 */
export function dreamDriftWearPet(
  petEntity: PetEntity,
  hero: DreamDriftHero,
): void {
  // Replace every slot while always retaining the required cape fallback.
  for (const slot of petAccessorySlots) {
    petEntity.setAccessory(slot, dreamDriftCostumes[hero][slot]);
  }
}
